/** A Job-less Visit the stamper could not build says so: held, backing off, or quarantined until a person retries. */
import { VisitBrowseRow, VisitConstructionAttention, VisitConstructionAttentionState } from '@jms/model';
import { DateTime } from 'luxon';
import { defineConditionalTag } from '../../shared/define.conditional.tag';

export const visitConstructionTag = defineConditionalTag<VisitBrowseRow>(row => {

  const attention = row?.jobCreationIssue;
  // The Job is the outcome the attention was waiting on; an overlay from an earlier read must not outlive it.
  if (!attention || row.job?._id) return null;
  if (attention.state === VisitConstructionAttentionState.NotCreated) {
    return { label: 'Missed · job not created', tone: 'error', ...tooltip([attention.reason?.trim(), lastAttemptLabel(attention)]) };
  }
  if (attention.state === VisitConstructionAttentionState.NeedsAttention) {
    return { label: 'Job not created · needs attention', tone: 'error', ...tooltip([attention.reason?.trim(), attemptsLabel(attention), lastAttemptLabel(attention)]) };
  }
  if (attention.state === VisitConstructionAttentionState.Retrying) {
    return { label: 'Creating job · retrying', tone: 'warning', ...tooltip([nextAttemptLabel(attention), attemptsLabel(attention)]) };
  }
  if (attention.state === VisitConstructionAttentionState.Held) {
    return { label: 'Creating job · on hold', tone: 'neutral', ...tooltip([attention.reason?.trim(), nextAttemptLabel(attention)]) };
  }
  return null;
});

function tooltip(parts: readonly (string | null | undefined)[]): { tooltip?: string } {

  const text = parts.filter(part => !!part).join(' · ');
  return text ? { tooltip: text } : {};
}

function attemptsLabel(attention: VisitConstructionAttention): string | null {

  const attempts = attention.attempts ?? 0;
  if (attempts <= 0) return null;
  return attempts === 1 ? '1 attempt' : `${attempts} attempts`;
}

function lastAttemptLabel(attention: VisitConstructionAttention): string | null {

  const at = viewerTime(attention.lastAttemptAt);
  return at ? `Last attempt ${at.toFormat('d LLL yyyy, h:mm a')}` : null;
}

function nextAttemptLabel(attention: VisitConstructionAttention): string | null {

  const at = viewerTime(attention.nextAttemptAt);
  if (!at) return null;
  // A retry whose time has come is waiting on the next stamper pass, not scheduled in the past.
  if (at.toMillis() <= Date.now()) return 'Next attempt due now';
  return `Next attempt ${at.toRelative()} (${at.toFormat('h:mm a')})`;
}

/** Stamper times are instants; they read in the viewer's zone, not the Visit's, because they describe the system, not the appointment. */
function viewerTime(value: Date | string | undefined): DateTime | null {

  if (!value) return null;
  const at = value instanceof Date ? DateTime.fromJSDate(value) : DateTime.fromISO(value);
  return at.isValid ? at : null;
}
